import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { catchError, tap } from "rxjs/operators";
import { LookupModel, LookupsClient, PagedListingResponseOfQuoteHeaderModel, QuoteClient, QuoteHeaderModel, UpdateQuoteStatusCommand } from "@core/services/api.service";
import {
    AssignVehicleTypeSuccessAction,
    GetQuoteByIdAction,
    GetQuoteByIdFailAction,
    GetQuoteByIdSuccessAction,
    LoadQuotesListAction,
    LoadQuotesListFailAction,
    LoadQuotesListSuccessAction,
    LoadQuoteStatusesAction,
    LoadQuoteStatusesFailAction,
    LoadQuoteStatusesSuccessAction,
    UpdateQuoteStatusAction,
    UpdateQuoteStatusFailAction,
    UpdateQuoteStatusSuccessAction
} from "./quotes.actions"

export class QuotesStateModel {
    public isLoading: boolean;
    public quote: QuoteHeaderModel;
    public quotesList: QuoteHeaderModel[];
    public quotesListing: PagedListingResponseOfQuoteHeaderModel;
    public quoteStatuses: LookupModel[];
}

@State<QuotesStateModel>({
    name: 'Quotes',
    defaults: {
        isLoading: true,
        quote: null,
        quotesList: [],
        quotesListing: null,
        quoteStatuses: []
    }
})
@Injectable()
export class QuotesState {
    constructor(private quoteClient: QuoteClient, private lookupsClient: LookupsClient) { }

    @Selector()
    static getQuote(state: QuotesStateModel) {
        return state.quote;
    }

    @Selector()
    static getQuotesList(state: QuotesStateModel) {
        return state.quotesList;
    }

    @Selector()
    static getQuotesListing(state: QuotesStateModel) {
        return state.quotesListing;
    }

    @Selector()
    static getQuoteStatuses(state: QuotesStateModel) {
        return state.quoteStatuses;
    }

    @Action(LoadQuoteStatusesAction)
    loadQuoteStatusesAction({ dispatch, patchState }: StateContext<QuotesStateModel>) {
        return this.lookupsClient.getQuoteStatusLookups().pipe(
            tap((quoteStatuses) => dispatch(new LoadQuoteStatusesSuccessAction({ quoteStatuses }))),
            catchError(() => dispatch(new LoadQuoteStatusesFailAction()))
        );
    }

    @Action(LoadQuoteStatusesSuccessAction)
    loadQuoteStatusesSuccessAction(ctx: StateContext<QuotesStateModel>, action: LoadQuoteStatusesSuccessAction) {
        ctx.patchState({ quoteStatuses: action.payload.quoteStatuses });
    }

    @Action(LoadQuoteStatusesFailAction)
    loadQuoteStatusesFailAction(ctx: StateContext<QuotesStateModel>) {
        ctx.patchState({ quoteStatuses: [] });
    }

    @Action(LoadQuotesListAction)
    loadQuotesListAction({ dispatch, patchState }: StateContext<QuotesStateModel>, action: LoadQuotesListAction) {
        patchState({ isLoading: true });
        return this.quoteClient.getQuotes(action.payload.getQuotesQuery).pipe(
            tap((quotesList) => dispatch(new LoadQuotesListSuccessAction({ quotesList }))),
            catchError((err) => dispatch(new LoadQuotesListFailAction()))
        );
    }

    @Action(LoadQuotesListSuccessAction)
    loadQuotesListSuccessAction(ctx: StateContext<QuotesStateModel>, action: LoadQuotesListSuccessAction) {
        ctx.patchState({
            isLoading: false,
            quotesList: action.payload.quotesList.results,
            quotesListing: action.payload.quotesList
        });
    }

    @Action(LoadQuotesListFailAction)
    loadQuotesListFailAction(ctx: StateContext<QuotesStateModel>) {
        ctx.patchState({
            isLoading: false,
            quotesList: [],
            quotesListing: null
        });
    }

    @Action(GetQuoteByIdAction)
    getQuoteByIdAction({ patchState, dispatch }: StateContext<QuotesStateModel>, action: GetQuoteByIdAction) {
        patchState({ isLoading: true });
        return this.quoteClient.getQuoteById(action.quoteId).pipe(
            tap((quote) => dispatch(new GetQuoteByIdSuccessAction(quote))),
            catchError(error => {
                dispatch(new GetQuoteByIdFailAction());
                throw error;
            })
        );
    }

    @Action(GetQuoteByIdSuccessAction)
    getQuoteByIdSuccessAction({ patchState }: StateContext<QuotesStateModel>, action: GetQuoteByIdSuccessAction) {
        patchState({ quote: action.payload });
    }

    @Action(GetQuoteByIdFailAction)
    getQuoteByIdFailAction(ctx: StateContext<QuotesStateModel>) {
        ctx.patchState({
            isLoading: false,
            quote: null
        });
    }

    @Action(UpdateQuoteStatusAction)
    updateQuoteStatusAction({ patchState, dispatch }: StateContext<QuotesStateModel>, action: UpdateQuoteStatusAction) {
        patchState({ isLoading: true });
        const command = new UpdateQuoteStatusCommand({ quoteId: action.payload.quoteId, orderStatusId: action.payload.orderStatusId });
        return this.quoteClient.updateQuoteStatus(command).pipe(
            tap(() => dispatch(new UpdateQuoteStatusSuccessAction(action.payload))),
            catchError((err) => {
                dispatch(new UpdateQuoteStatusFailAction());
                throw err;
            })
        );
    }

    @Action(UpdateQuoteStatusSuccessAction)
    updateQuoteStatusSuccessAction({ getState, patchState }: StateContext<QuotesStateModel>, action: UpdateQuoteStatusSuccessAction) {
        const state = getState();
        const updatedList = (state.quotesList || []).map(q =>
            q.id === action.payload.quoteId
                ? { ...q, orderStatusId: action.payload.orderStatusId, orderStatusDescription: action.payload.orderStatusDescription } as QuoteHeaderModel
                : q
        );
        const updatedQuote = state.quote?.id === action.payload.quoteId
            ? { ...state.quote, orderStatusId: action.payload.orderStatusId, orderStatusDescription: action.payload.orderStatusDescription } as QuoteHeaderModel
            : state.quote;
        patchState({ isLoading: false, quotesList: updatedList, quote: updatedQuote });
    }

    @Action(UpdateQuoteStatusFailAction)
    updateQuoteStatusFailAction({ patchState }: StateContext<QuotesStateModel>) {
        patchState({ isLoading: false });
    }

    @Action(AssignVehicleTypeSuccessAction)
    assignVehicleTypeSuccessAction({ getState, patchState }: StateContext<QuotesStateModel>, action: AssignVehicleTypeSuccessAction) {
        const state = getState();
        const { quoteId, area, vehicleTypeId, vehicleTypeName, estimatedKm, estimatedRatePerKm, estimatedTotal, numberOfDrivers, loadingCountry, dischargeCountry, carrier } = action.payload;
        const updatedList = (state.quotesList || []).map(q =>
            q.id === quoteId
                ? { ...q, area, vehicleTypeId, vehicleTypeName, estimatedKm, estimatedRatePerKm, estimatedTotal, numberOfDrivers, loadingCountry, dischargeCountry, carrier } as QuoteHeaderModel
                : q
        );
        const updatedQuote = state.quote?.id === quoteId
            ? { ...state.quote, area, vehicleTypeId, vehicleTypeName, estimatedKm, estimatedRatePerKm, estimatedTotal, numberOfDrivers, loadingCountry, dischargeCountry, carrier } as QuoteHeaderModel
            : state.quote;
        patchState({ quotesList: updatedList, quote: updatedQuote });
    }
}
